<script lang="ts">
  import {project, download, downloadDialog, draftStore, step, user} from '@/store';

  import Dialog from '@/components/dialog/Dialog.svelte';
  import {Features, STEP} from '@/types/common';
  import {vidDialogOpen} from '../thumbPreview/viddialog-store';
  import Descriptions from './Descriptions.svelte';
  import {onDestroy} from 'svelte';
  import Swal from 'sweetalert2';
  import {canAccess} from '@/utils/common';

  export let isDownloadDialogOpen = true;
  export let progress = 0;

  let title = 'Sample Download';
  $: title = $draftStore?.projectName || ($project.video?.title as string);

  let downloadStarted = false;
  let downloadProgress = 0;
  let completeRequest: string = '';
  let downloadUrl: string = '';
  let showCloseButton: boolean = false;
  let isDownloaded: boolean = false;

  $: downloadUrl = $download.downloadableUrl || '';
  $: completeRequest = $download.completedRequestId ? $download.downloadableUrl || '#' : '#';
  $: if (downloadUrl != '' && !downloadStarted) {
    progress = 100;
  }

  async function exportToSermonSend(notify: boolean = true) {
    try {
      await project.createDownloadable($draftStore.currentCloudDraftId as number, downloadUrl);
      if (notify) {
        Swal.fire({
          icon: 'success',
          title: 'Exported to Sermon Send',
          showConfirmButton: false,
          timer: 1500,
          toast: true,
          position: 'bottom-end'
        });
      }
    } catch (error) {
      console.error('Failed to export to Sermon Send', error);
      if (notify) {
        Swal.fire({
          icon: 'error',
          title: 'Failed to export to Sermon Send',
          text: 'Something went wrong while exporting to Sermon Send',
          showConfirmButton: false,
          timer: 3000,
          toast: true,
          position: 'bottom-end'
        });
      }
    }
  }

  function downloadClip(downloadString: string) {
    progress = 0;
    isDownloaded = false;

    if (!downloadString) {
      return console.error('Unable to download null video string');
    }

    const filename = `${title}-SermonShots-${Date.now()}.mp4`;
    const xhr = new XMLHttpRequest();
    xhr.open('GET', completeRequest, true);
    xhr.responseType = 'blob';
    downloadStarted = true;

    exportToSermonSend(false);

    xhr.onprogress = event => {
      if (!event.lengthComputable) return;
      const percentComplete = parseInt(((event.loaded / event.total) * 100).toFixed());
      progress = isNaN(percentComplete) ? 0 : percentComplete;
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        const blob = xhr.response;
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        a.remove();
        isDownloaded = true;
      } else {
        console.error('Request failed with status:', xhr.status);
      }

      progress = 0;
      downloadStarted = false;
    };

    xhr.onerror = err => {
      progress = 0;
      downloadStarted = false;
      showCloseButton = true;
      if (err) console.error('Problem downloading tracking video using javascript', err);
      window.open(completeRequest as string);
    };

    xhr.send();
  }

  function back2Step1() {
    closeDialog();
    step.update(STEP.CHOOSE_VIDEO);
    vidDialogOpen.set(true);
  }

  function closeDialog() {
    isDownloaded = false;
    downloadDialog.set(false);
  }
</script>

<Dialog isDialogOpen={isDownloadDialogOpen} useIfShow={true}>
  <div class="pointer-events-none flex h-full w-full items-end justify-center lg:items-center">
    <div
      class="pointer-events-auto flex w-full min-w-[280px] max-w-[880px] flex-col rounded-2xl border border-zinc-400 bg-white p-12 shadow-lg dark:border-zinc-600 dark:bg-zinc-700 {isDownloaded
        ? 'max-h-[80vh]'
        : ''}"
      style="z-index:99;"
    >
      {#if isDownloaded}
        <!-- Header -->
        <div class="flex w-full gap-2">
          {#if downloadUrl !== '' || showCloseButton}
            <button on:click={closeDialog} class="ml-auto rounded-full px-2 hover:bg-zinc-100">
              <i class="fa-solid fa-xmark text-lg text-black" aria-hidden="true"></i>
            </button>
          {/if}
        </div>
        <!-- Action Buttons -->
        <div class="mb-4 flex w-full flex-col gap-4 px-10 text-center">
          <div class="content-center align-middle">
            <i class="fa-solid fa-circle-check text-6xl font-bold text-primary opacity-80"></i>
          </div>
          <div class="content-center align-middle">
            <h2 class="text-2xl font-bold text-primary">Sermon Clip Downloaded!</h2>
          </div>
          <div class="content-center align-middle">
            <p class="leading-loose text-zinc-500">
              Your video has been successfully downloaded and is ready to go. Ready to dive into your next creative
              project?
            </p>
          </div>
          <div class="content-center justify-center space-x-2 align-middle">
            <button on:click={closeDialog} class="rounded-lg border bg-white py-2 font-medium text-zinc-500 lg:w-auto">
              Back to Editor
            </button>
            <button on:click={() => back2Step1()} class="rounded-lg bg-primary py-2 font-medium text-white lg:w-auto">
              Create New
            </button>
          </div>
        </div>
        <div class="px-6">
          <hr />
        </div>
        <!-- Content -->
        <img
          class="h-full w-full rounded-2xl object-cover"
          src="https://storage.googleapis.com/sermonshots-local-storage/images/download-popup.webp"
          alt="download_popup"
        />
      {:else}
        <!-- Header -->
        <div class="flex w-full gap-2 pb-4">
          <span class="text-2xl font-bold">Download Clip</span>
          {#if downloadUrl !== '' || showCloseButton}
            <button on:click={closeDialog} class="ml-auto rounded-full px-2 hover:bg-zinc-100">
              <i class="fa-solid fa-xmark p-2 text-lg text-black" aria-hidden="true"></i>
            </button>
          {/if}
        </div>
        <div class="flex w-full flex-col gap-6 py-2">
          <div class="flex w-full items-center gap-4">
            <p class="truncate">{title}</p>
            <div class="ml-auto rounded-full bg-zinc-100 px-2 py-1 text-sm font-medium text-zinc-900 hover:bg-zinc-100">
              {#if downloadStarted}
                Downloading...
              {:else if progress == 0 && !downloadStarted}
                <span class="flex space-x-2 text-sm">
                  <i class="fa-duotone fa-solid fa-spinner animate-spin"></i>
                  <p class="text-zinc-500">You are in queue</p>
                </span>
              {:else if progress > 98 && downloadUrl == ''}
                <span class="flex space-x-2 text-sm">
                  <i class="fa-duotone fa-solid fa-spinner animate-spin"></i>
                  <p class="text-zinc-500">Finalizing...</p>
                </span>
              {:else}
                {downloadUrl !== '' ? 'Complete' : 'Rendering...'}
              {/if}
            </div>
          </div>
          <!-- Progress Bar -->
          <div class="flex w-full">
            <button
              class="relative h-5 w-full overflow-hidden rounded-full rounded-lg bg-zinc-300 text-center font-medium"
            >
              <div
                class="absolute left-0 top-0 h-5 rounded-md bg-gradient-to-r from-primary to-secondary text-white"
                style="width: {progress}%;"
              >
                {progress}%
              </div>
            </button>
          </div>
          <!-- Action Buttons -->
          <div class="flex justify-end gap-2">
            {#if canAccess($user, Features.SERMON_SHARE)}
              <button
                on:click={() => exportToSermonSend()}
                class="relative overflow-hidden rounded-lg border border-zinc-200 bg-white text-center font-medium text-zinc-700 {downloadUrl !==
                ''
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed opacity-25'}"
                disabled={downloadUrl !== '' || downloadStarted ? false : true}
              >
                <div class="w-full px-4 py-2">Export to Sermon Send</div>
              </button>
            {/if}
            <button
              on:click={() => downloadClip(completeRequest)}
              class="relative overflow-hidden rounded-lg bg-primary text-center font-medium text-zinc-100 {downloadUrl !==
              ''
                ? 'cursor-pointer'
                : 'cursor-not-allowed opacity-25'}"
              disabled={downloadUrl !== '' || downloadStarted ? false : true}
            >
              <div class="w-full px-4 py-2">Download Clip</div>
            </button>
          </div>
        </div>
        <!-- Content -->
        <Descriptions bind:draftId={$draftStore.currentCloudDraftId} />
      {/if}
    </div>
  </div>
</Dialog>
